import { Switch, Route, Redirect } from "wouter";
import { VoiceCallProvider } from "@/components/voice-call/VoiceCallProvider";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { useWebSocket } from "@/hooks/useWebSocket";
import { trackPageView } from "@/lib/analytics";
import { useLocation } from "wouter";
import { useEffect, useRef, useState, Suspense, lazy } from "react";
import MultiChatContainer from "@/components/multi-chat-container";

import { NotificationPopup } from "@/components/notification-popup";

// Lazy load pages
const NotFound = lazy(() => import("@/pages/not-found"));
const Home = lazy(() => import("@/pages/home"));
const HomeModern = lazy(() => import("@/pages/home-modern"));
const HomeCercAmanti = lazy(() => import("@/pages/home-cercamanti"));
const Search = lazy(() => import("@/pages/search"));
const SearchUsers = lazy(() => import("@/pages/search-users"));
const Messages = lazy(() => import("@/pages/messages"));
const ChatPage = lazy(() => import("@/pages/chat"));
const Profile = lazy(() => import("@/pages/profile"));
const UserProfile = lazy(() => import("@/pages/user-profile"));

const Admin = lazy(() => import("@/pages/admin"));
const AdminSettings = lazy(() => import("@/pages/admin-settings"));
const AdminAnalyticsSettings = lazy(() => import("@/pages/admin-analytics-settings"));
const AdminLogin = lazy(() => import("@/pages/admin-login"));
const AdminUsers = lazy(() => import("@/pages/admin-users"));
const AdminMonetization = lazy(() => import("@/pages/admin-monetization"));
const AdminContent = lazy(() => import("@/pages/admin-content"));
const AdminConversations = lazy(() => import("@/pages/admin-conversations"));
const AdminDashboard = lazy(() => import("@/pages/admin-dashboard"));
const AdminPayments = lazy(() => import("@/pages/admin-payments"));
const AdminIcebreaker = lazy(() => import("@/pages/admin-icebreaker"));
const AdminSecurity = lazy(() => import("@/pages/admin-security"));
const AdminAIProfiles = lazy(() => import("@/pages/admin-ai-profiles"));
const AdminGalleryGen = lazy(() => import("@/pages/admin-gallery-gen"));
const AdminAiMonitor = lazy(() => import("@/pages/admin-ai-monitor"));
const AdminAiBehavior = lazy(() => import("@/pages/admin-ai-behavior"));
const AdminEvals = lazy(() => import("@/pages/admin-evals"));
const AdminGuardrails = lazy(() => import("@/pages/admin-guardrails"));
const AdminMessagesSafety = lazy(() => import("@/pages/admin-messages-safety"));
const AdminChatPhotoLogs = lazy(() => import("@/pages/admin-chat-photo-logs"));
const AdminRoles = lazy(() => import("@/pages/admin-roles"));
const AdminSystemHealth = lazy(() => import("@/pages/admin-system-health"));

const Scopri = lazy(() => import("@/pages/scopri"));
const Favorites = lazy(() => import("@/pages/favorites"));
const Connections = lazy(() => import("@/pages/connections"));
const Notifications = lazy(() => import("@/pages/notifications"));
const PremiumStatus = lazy(() => import("@/pages/premium-status"));
const Checkout = lazy(() => import("@/pages/checkout"));
const PremiumSuccess = lazy(() => import("@/pages/premium-success"));
const PremiumCancelled = lazy(() => import("@/pages/premium-cancelled"));
const PremiumDeclined = lazy(() => import("@/pages/premium-declined"));
const Rewards = lazy(() => import("@/pages/rewards"));
const CreditHistory = lazy(() => import("@/pages/credit-history"));

const ResetPassword = lazy(() => import("@/pages/reset-password"));
const AccountSettings = lazy(() => import("@/pages/account-settings"));
const GestioneAbbonamento = lazy(() => import("@/pages/gestione-abbonamento"));
const HomeLandingPage = lazy(() => import("@/pages/home-landing-page"));
const LaunchPage = lazy(() => import("@/pages/launch-page"));
const PrivatePhotos = lazy(() => import("@/pages/private-photos"));
const ActivateAccount = lazy(() => import("@/pages/activate-account"));
const CheckEmail = lazy(() => import("@/pages/check-email"));
const AffiliateLanding = lazy(() => import("@/pages/affiliate-landing"));
const Onboarding = lazy(() => import("@/pages/onboarding"));
const AmantiAI = lazy(() => import("@/pages/amanti-ai"));
const FAQ = lazy(() => import("@/pages/faq"));
const Contatti = lazy(() => import("@/pages/contatti"));
const Privacy = lazy(() => import("@/pages/privacy"));
const TerminiEPolicy = lazy(() => import("@/pages/termini-e-policy"));
const Termini = lazy(() => import("@/pages/termini"));
const CookiePolicyPage = lazy(() => import("@/pages/cookie-policy"));
const Reclami = lazy(() => import("@/pages/reclami"));
const LineeGuidaCommunity = lazy(() => import("@/pages/linee-guida-community"));
const USC2257 = lazy(() => import("@/pages/usc-2257"));
const DMCA = lazy(() => import("@/pages/dmca"));
const ContenutiVietati = lazy(() => import("@/pages/contenuti-vietati"));
const RimozioneContenuti = lazy(() => import("@/pages/rimozione-contenuti"));
const ComingSoon = lazy(() => import("@/pages/coming-soon"));
const AffiliateApply = lazy(() => import("@/pages/affiliate-apply"));

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
          <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
        <div className="text-gray-600 text-lg">Caricamento...</div>
      </div>
    </div>
  );
}
import { useGeoBlock } from "@/hooks/useGeoBlock";
import { AgeGate } from "@/components/age-gate";
import { CookieBanner } from "@/components/cookie-banner";
import { MarketingConsentBanner } from "@/components/marketing-consent-banner";
import { PushOptInPrompt } from "@/components/push-optin-prompt";
// Modale disclaimer AI disattivata: l'informativa resta nella striscia in cima a ogni chat AI.
// import { AiDisclaimerPrompt } from "@/components/ai-disclaimer-prompt";
import { SiteFooter } from "@/components/site-footer";
import { SuspensionBanner } from "@/components/SuspensionBanner";
import { AdminGate } from "@/components/AdminGate";
import { OnboardingGate } from "@/components/OnboardingGate";
import { ThemeProvider } from "@/components/theme-provider";
import { ForceLightTheme } from "@/components/force-light-theme";

/**
 * Avvolge una pagina nell'OnboardingGate mantenendo l'API `component={...}` di
 * wouter. I wrapper sono memoizzati in un modulo-level cache: se li creassimo
 * al volo a ogni render, React vedrebbe un tipo di componente diverso ogni
 * volta e smonterebbe/rimonterebbe la pagina (perdendo stato e rifacendo le
 * fetch) a ogni aggiornamento del Router.
 */
const gatedCache = new WeakMap<React.ComponentType<any>, React.ComponentType<any>>();
function gated<P extends object>(Component: React.ComponentType<P>) {
  const cached = gatedCache.get(Component);
  if (cached) return cached;
  const Wrapped = (props: P) => (
    <OnboardingGate>
      <Component {...props} />
    </OnboardingGate>
  );
  gatedCache.set(Component, Wrapped);
  return Wrapped;
}

function Router() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const [location] = useLocation();
  const prevLocationRef = useRef<string>(location);

  const { data: publicSettings } = useQuery({
    queryKey: ["/api/settings/public"],
    staleTime: 5 * 60 * 1000,
  });
  const liveMode = Array.isArray(publicSettings)
    ? (publicSettings as any[]).find((s) => s.key === "live_mode")?.value ?? "true"
    : "true";
  const isAdmin = !!(user as any)?.isAdmin;

  // Pre-launch preview bypass: a ?preview=CODE link (or a previously validated
  // session) lets non-admins through the launch page. The code is secret, so it is
  // verified server-side (/api/preview/verify) — never compared in the client.
  //
  // sessionStorage, not localStorage: access dies with the browser tab, so a
  // shared preview link doesn't grant that browser permanent entry. A new tab
  // needs the ?preview= link again. Kept in storage (rather than requiring the
  // param on every page) because otherwise the first internal link would drop
  // the query string and bounce the visitor back to the launch page.
  const [previewOk, setPreviewOk] = useState<boolean>(
    () => (typeof window !== "undefined" && sessionStorage.getItem("preview_ok") === "1")
  );
  useEffect(() => {
    // Drop the pre-existing localStorage grant: it was permanent, and browsers
    // that already have it would otherwise never lose access.
    localStorage.removeItem("preview_ok");

    const params = new URLSearchParams(window.location.search);
    const code = params.get("preview");
    if (!code || previewOk) return;
    fetch("/api/preview/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d?.valid) {
          sessionStorage.setItem("preview_ok", "1");
          setPreviewOk(true);
        }
      })
      .catch(() => {});
  }, [previewOk]);

  const showLaunchPage = liveMode !== "true" && !isAdmin && !previewOk;

  // Track page views and reset scroll when routes change
  useEffect(() => {
    if (location !== prevLocationRef.current) {
      trackPageView(location);
      window.scrollTo(0, 0);
      prevLocationRef.current = location;
    }
  }, [location]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Switch>
        {!isAuthenticated ? (
          <>
            <Route
              path="/"
              component={() => (
                showLaunchPage ? (
                  <LaunchPage />
                ) : (
                  <HomeLandingPage isAuthenticated={isAuthenticated} />
                )
              )}
            />
            {/* /auth redirects to landing with login modal */}
            <Route path="/auth">
              {() => {
                window.location.href = "/?login=true";
                return null;
              }}
            </Route>
            {/* Vecchi flussi di registrazione: raccoglievano campi che ora non
                si chiedono piu' (nome, citta', nickname) e inviavano un body
                che il nuovo /api/register rifiuta. Reindirizzano al modale
                sulla landing, che e' l'unico flusso mantenuto. */}
            <Route path="/register">{() => <Redirect to="/?register=true" />}</Route>
            <Route path="/register-wizard">{() => <Redirect to="/?register=true" />}</Route>
            {/* /forgot-password redirects to landing with forgot password modal */}
            <Route path="/forgot-password">
              {() => {
                window.location.href = "/?forgot-password=true";
                return null;
              }}
            </Route>
            <Route path="/reset-password" component={ResetPassword} />

          </>
        ) : (
          <>
            {/* Onboarding: destinazione del gate, quindi NON protetta da esso.
                Deve stare prima delle altre, altrimenti il gate rimbalzerebbe
                l'utente su se stesso all'infinito. */}
            <Route path="/onboarding" component={Onboarding} />

            {/* Le rotte dell'app sono avvolte da OnboardingGate una per una:
                wouter vuole <Route> come figli diretti di <Switch>, quindi un
                wrapper attorno al fragment romperebbe il matching. */}
            <Route path="/" component={gated(Search)} />
            {/* <Route path="/home" component={HomeModern} /> */}
            <Route path="/search" component={gated(Search)} />
            <Route path="/search-users" component={gated(SearchUsers)} />
            <Route path="/messages" component={gated(Messages)} />
            <Route path="/chat/:userId" component={gated(ChatPage)} />
            <Route path="/profile" component={gated(Profile)} />
            <Route path="/profile/:userId" component={gated(UserProfile)} />
            <Route path="/scopri" component={gated(Scopri)} />
            <Route path="/favorites" component={gated(Favorites)} />
            <Route path="/connections" component={gated(Connections)} />

            <Route path="/notifications" component={gated(Notifications)} />


            <Route path="/premium-status" component={gated(PremiumStatus)} />
            <Route path="/checkout" component={gated(Checkout)} />
            <Route path="/premium-success" component={gated(PremiumSuccess)} />
            <Route path="/premium-cancelled" component={gated(PremiumCancelled)} />
            <Route path="/premium-declined" component={gated(PremiumDeclined)} />
            <Route path="/rewards" component={gated(Rewards)} />
            <Route path="/credit-history" component={gated(CreditHistory)} />
            <Route path="/credits">{() => <Redirect to="/credit-history" />}</Route>
            <Route path="/gestione-abbonamento" component={gated(GestioneAbbonamento)} />
            <Route path="/account-settings" component={gated(AccountSettings)} />
            <Route path="/private-photos" component={gated(PrivatePhotos)} />
          </>
        )}

        <Route path="/activate" component={ActivateAccount} />
        {/* Fuori dai rami autenticato/non: si arriva qui subito dopo la
            registrazione, quando la sessione non esiste ancora. */}
        <Route path="/check-email" component={CheckEmail} />
        {/* Landing affiliati: fuori dai rami autenticato/non e prima del gate
            di lancio, perche' il traffico a pagamento deve vedere il form
            anche mentre il sito e' in pre-lancio. */}
        <Route path="/lp" component={AffiliateLanding} />
        <Route path="/amanti-ai" component={AmantiAI} />
        <Route path="/faq" component={FAQ} />
        <Route path="/contatti" component={Contatti} />
        <Route path="/termini-e-policy" component={TerminiEPolicy} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/termini" component={Termini} />
        <Route path="/cookie-policy" component={CookiePolicyPage} />
        <Route path="/reclami" component={Reclami} />
        <Route path="/linee-guida-community" component={LineeGuidaCommunity} />
        <Route path="/2257" component={USC2257} />
        <Route path="/dmca" component={DMCA} />
        <Route path="/contenuti-vietati" component={ContenutiVietati} />
        <Route path="/rimozione-contenuti" component={RimozioneContenuti} />
        <Route path="/affiliate-apply" component={AffiliateApply} />
        {/* Admin login pages — never gated */}
        <Route path="/admin/login" component={AdminLogin} />
        <Route path="/admin-login" component={AdminLogin} />
        {/* Protected admin routes — wrapped in AdminGate which redirects to
            /admin-login if /api/admin/check fails. */}
        <Route path="/admin">{() => <AdminGate><Admin /></AdminGate>}</Route>
        <Route path="/admin/dashboard">{() => <AdminGate allowedRoles={["admin"]}><AdminDashboard /></AdminGate>}</Route>
        <Route path="/admin/settings">{() => <AdminGate allowedRoles={["admin"]}><AdminSettings /></AdminGate>}</Route>
        <Route path="/admin/analytics-settings">{() => <AdminGate allowedRoles={["admin"]}><AdminAnalyticsSettings /></AdminGate>}</Route>
        <Route path="/admin/users">{() => <AdminGate allowedRoles={["admin"]}><AdminUsers /></AdminGate>}</Route>
        <Route path="/admin/content">{() => <AdminGate allowedRoles={["admin"]}><AdminContent /></AdminGate>}</Route>
        <Route path="/admin/conversations">{() => <AdminGate allowedRoles={["admin"]}><AdminConversations /></AdminGate>}</Route>
        <Route path="/admin/payments">{() => <AdminGate allowedRoles={["admin"]}><AdminPayments /></AdminGate>}</Route>
        <Route path="/admin/icebreaker">{() => <AdminGate allowedRoles={["admin"]}><AdminIcebreaker /></AdminGate>}</Route>
        <Route path="/admin/monetization">{() => <AdminGate allowedRoles={["admin"]}><AdminMonetization /></AdminGate>}</Route>
        <Route path="/admin/security">{() => <AdminGate allowedRoles={["admin"]}><AdminSecurity /></AdminGate>}</Route>
        <Route path="/admin/roles">{() => <AdminGate allowedRoles={["admin"]}><AdminRoles /></AdminGate>}</Route>
        <Route path="/admin/system-health">{() => <AdminGate allowedRoles={["admin"]}><AdminSystemHealth /></AdminGate>}</Route>
        <Route path="/admin/ai-profiles">{() => <AdminGate allowedRoles={["admin"]}><AdminAIProfiles /></AdminGate>}</Route>
        <Route path="/admin/gallery-gen">{() => <AdminGate allowedRoles={["admin"]}><AdminGalleryGen /></AdminGate>}</Route>
        <Route path="/admin/ai-monitor">{() => <AdminGate allowedRoles={["admin"]}><AdminAiMonitor /></AdminGate>}</Route>
        <Route path="/admin/ai-behavior">{() => <AdminGate allowedRoles={["admin"]}><AdminAiBehavior /></AdminGate>}</Route>
        <Route path="/admin/evals">{() => <AdminGate allowedRoles={["admin"]}><AdminEvals /></AdminGate>}</Route>
        <Route path="/admin/guardrails">{() => <AdminGate allowedRoles={["admin"]}><AdminGuardrails /></AdminGate>}</Route>
        <Route path="/admin/messages-safety">{() => <AdminGate><AdminMessagesSafety /></AdminGate>}</Route>
        <Route path="/admin/chat-photo-logs">{() => <AdminGate allowedRoles={["admin"]}><AdminChatPhotoLogs /></AdminGate>}</Route>


        {/* If not authenticated and on unknown route, save intended destination then redirect to landing with login */}
        <Route>{() => {
          if (!isAuthenticated) {
            const path = window.location.pathname + window.location.search;
            if (path !== "/") {
              sessionStorage.setItem("returnTo", path);
            }
            return <Redirect to="/?login=true" />;
          }
          return <NotFound />;
        }}</Route>
      </Switch>
    </Suspense>
  );
}

function AppWithMessenger() {
  const { isAuthenticated } = useAuth();
  const [location] = useLocation();

  // Initialize WebSocket connection for real-time notifications
  useWebSocket();

  // Hide footer on pages that already have their own footer, chat, and admin pages
  const hideFooter = location.startsWith('/chat/') ||
    location.startsWith('/admin') ||
    location === '/faq' ||
    location === '/termini-e-policy' ||
    location === '/privacy' ||
    location === '/termini' ||
    location === '/cookie-policy' ||
    location === '/reclami' ||
    location === '/2257' ||
    location === '/dmca' ||
    location === '/contenuti-vietati' ||
    location === '/rimozione-contenuti' ||
    location === '/amanti-ai' ||
    location === '/contatti' ||
    // La landing affiliati ha una sua chiusura: il footer del sito
    // aggiungerebbe decine di link in uscita sotto il form.
    location === '/lp';

  return (
    <VoiceCallProvider>
      <Router />

      {/* Multi Chat Container - Sistema per gestire fino a 3 chat simultanee */}
      {isAuthenticated && <MultiChatContainer />}

      {/* Unread Messages Indicator */}


      {/* Notification Popup */}
      {isAuthenticated && <NotificationPopup />}

      {/* Suspension banner — shown only when user.suspendedAt is set */}
      {isAuthenticated && <SuspensionBanner />}

      {/* Site Footer for authenticated pages */}
      {isAuthenticated && !hideFooter && <SiteFooter />}
    </VoiceCallProvider>
  );
}

function App() {
  // const { isBlocked, isLoading: geoLoading } = useGeoBlock();

  // Analytics initialized via cookie consent (cookie-banner.tsx)
  useEffect(() => {
    // Handle unhandled promise rejections to prevent 401 error toasts
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      // Block ALL authentication-related errors
      const reason = String(event.reason?.message || event.reason || '');
      if (reason.includes('401') ||
        reason.includes('Non autenticato') ||
        reason.includes('Non autorizzato') ||
        reason.includes('SILENT_401') ||
        reason.includes('analytics') ||
        reason.includes('fetch')) {
        event.preventDefault();
        event.stopPropagation();
        console.log('Blocked error toast:', reason);
        return false;
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Show loading while checking geo
  // if (geoLoading) {
  //   return (
  //     <div className="flex items-center justify-center min-h-screen bg-gray-50">
  //       <div className="text-center">
  //         <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4">
  //           <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  // Show Coming Soon for non-Italian IPs
  // if (isBlocked) {
  //   return <ComingSoon />;
  // }

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ForceLightTheme />
          <AppWithMessenger />
          {/* Gate 18+ all'ingresso del sito: sopra ogni altro overlay, mostrato
              ai soli visitatori anonimi (chi ha un account ha gia' dichiarato
              la data di nascita in registrazione). */}
          <AgeGate />
          <CookieBanner />
          <MarketingConsentBanner />
          <PushOptInPrompt />
          {/* <AiDisclaimerPrompt /> */}
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
